import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Paper, Button, IconButton, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getRoles, deleteRole } from "app/api/access-rights.api";
import RoleEditorDialog from "../components/role-editor.dialog";

const useStyles = makeStyles(() => ({
  pageHead: { marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: "#101828", lineHeight: "30px" },
  pageSubtitle: { fontSize: 14, color: "#667085", marginTop: 4, maxWidth: 640 },
  addBtn: {
    backgroundColor: "#3A4D54", color: "#fff", fontWeight: 600, fontSize: 14,
    textTransform: "none", borderRadius: 8, padding: "8px 18px",
    "&:hover": { backgroundColor: "#2d3b41" },
  },
  card: { border: "1px solid #E4E7EC", borderRadius: 12, boxShadow: "0 1px 2px rgba(16,24,40,0.05)", overflow: "hidden" },
  tableHead: {
    "& .MuiTableCell-head": {
      fontSize: 11.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase",
      color: "#667085", backgroundColor: "#FCFCFD", borderBottom: "1px solid #E4E7EC", padding: "10px 16px",
    },
  },
  roleName: { fontSize: 14, fontWeight: 600, color: "#101828" },
  roleDesc: { fontSize: 12.5, color: "#667085", marginTop: 1 },
  systemChip: { fontSize: 11, height: 20, backgroundColor: "#EEF4FF", color: "#3538CD", marginLeft: 8 },
  countText: { fontSize: 14, color: "#344054", fontVariantNumeric: "tabular-nums" },
  loadingBox: { display: "flex", justifyContent: "center", padding: "40px 24px" },
  emptyState: { padding: "28px 24px", textAlign: "center", color: "#98A2B3", fontSize: 14 },
}));

export default function AccessRightsPage() {
  const classes = useStyles();
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { uuid, is_system } | null
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getRoles();
      setRoles(res.data.data || []);
    } catch (e) {
      setSnack({ severity: "error", message: e.response?.data?.message || "Failed to load roles" });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (role) => { setEditing({ uuid: role.uuid, is_system: role.is_system }); setEditorOpen(true); };

  const handleSaved = () => {
    setEditorOpen(false);
    setSnack({ severity: "success", message: "Role saved." });
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteRole(deleteTarget.uuid);
      setSnack({ severity: "success", message: "Role deleted." });
      setDeleteTarget(null);
      load();
    } catch (e) {
      setSnack({ severity: "error", message: e.response?.data?.message || "Failed to delete role" });
    }
    setIsDeleting(false);
  };

  return (
    <Box>
      <Box className={classes.pageHead}>
        <Box>
          <Typography className={classes.pageTitle}>Access Rights</Typography>
          <Typography className={classes.pageSubtitle}>
            Manage the roles assigned to <b>partner portal users</b> — each role bundles the access rights a
            partner user gets. Create roles here, then assign them to users from each partner's Users tab.
            (These do not affect admin access.)
          </Typography>
        </Box>
        <Button className={classes.addBtn} startIcon={<AddIcon />} onClick={openNew} disableElevation>
          New Role
        </Button>
      </Box>

      <Paper className={classes.card} elevation={0}>
        {isLoading ? (
          <Box className={classes.loadingBox}><CircularProgress size={24} style={{ color: "#3A4D54" }} /></Box>
        ) : roles.length === 0 ? (
          <Typography className={classes.emptyState}>No roles yet. Create one to get started.</Typography>
        ) : (
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell align="right">Access Rights</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right" style={{ paddingRight: 24 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.uuid} hover>
                  <TableCell style={{ padding: "12px 16px" }}>
                    <Box display="flex" alignItems="center">
                      <Box>
                        <Typography className={classes.roleName}>
                          {role.name}
                          {role.is_system && <Chip label="Built-in" size="small" className={classes.systemChip} />}
                        </Typography>
                        {role.description && <Typography className={classes.roleDesc}>{role.description}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right" className={classes.countText}>{role.access_rights_count}</TableCell>
                  <TableCell align="right" className={classes.countText}>{role.users_count}</TableCell>
                  <TableCell align="right" style={{ paddingRight: 16 }}>
                    <IconButton size="small" onClick={() => openEdit(role)} aria-label="Edit role">
                      <EditIcon style={{ fontSize: 18, color: "#475467" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(role)}
                      disabled={role.is_system || role.users_count > 0}
                      aria-label="Delete role"
                      title={role.is_system ? "Built-in roles cannot be deleted" : role.users_count > 0 ? "Reassign users before deleting" : "Delete role"}
                    >
                      <DeleteOutlineIcon style={{ fontSize: 18, color: role.is_system || role.users_count > 0 ? "#D0D5DD" : "#B42318" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {editorOpen && (
        <RoleEditorDialog
          open={editorOpen}
          roleUuid={editing?.uuid}
          isSystem={editing?.is_system}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
        />
      )}

      <Dialog open={!!deleteTarget} onClose={() => !isDeleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 700, fontSize: 15, color: "#B42318" }}>Delete Role</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14, color: "#344054" }}>
            Delete <b>{deleteTarget?.name}</b>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting} style={{ color: "#344054", fontWeight: 600, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={isDeleting}
            disableElevation
            style={{ backgroundColor: "#B42318", color: "#fff", fontWeight: 600, textTransform: "none", borderRadius: 8 }}
          >
            {isDeleting ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity || "info"} onClose={() => setSnack(null)}>{snack?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
